import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Fade,
  useTheme,
  Divider,
  Chip
} from '@mui/material';
import { 
  CheckCircleOutline, 
  RadioButtonUnchecked, 
  ErrorOutline, 
  HourglassTopTwoTone,
  LightbulbOutlined,
  SpeedOutlined,
  CodeOutlined,
  DevicesOutlined,
  BugReportOutlined,
  LinkOutlined,
  JavascriptOutlined,
  ImageOutlined,
  AssessmentOutlined
} from '@mui/icons-material';

/**
 * Steps for PageSpeed analysis process with icons and descriptions
 */
const ANALYSIS_STEPS = [
  { 
    id: 'initializing', 
    label: 'Preparing Analysis', 
    description: 'Setting up the analysis environment',
    icon: LightbulbOutlined 
  },
  { 
    id: 'fetching', 
    label: 'Fetching Page Data', 
    description: 'Retrieving website content and structure',
    icon: DevicesOutlined 
  },
  { 
    id: 'analyzing', 
    label: 'Running Performance Analysis', 
    description: 'Evaluating core web vitals and performance metrics',
    icon: SpeedOutlined 
  },
  { 
    id: 'technologies', 
    label: 'Detecting Technologies', 
    description: 'Identifying frameworks, libraries and tools used',
    icon: CodeOutlined 
  },
  { 
    id: 'issues', 
    label: 'Identifying Issues', 
    description: 'Scanning for potential performance issues',
    icon: BugReportOutlined 
  },
  { 
    id: 'issues-links', 
    label: 'Checking Links', 
    description: 'Verifying all page links are working correctly',
    icon: LinkOutlined 
  },
  { 
    id: 'issues-scripts', 
    label: 'Analyzing Scripts', 
    description: 'Checking script loading and execution efficiency',
    icon: JavascriptOutlined 
  },
  { 
    id: 'issues-images', 
    label: 'Optimizing Images', 
    description: 'Verifying image sizes and formats are optimized',
    icon: ImageOutlined 
  },
  { 
    id: 'finalizing', 
    label: 'Finalizing Results', 
    description: 'Compiling final report and recommendations',
    icon: AssessmentOutlined 
  },
  { 
    id: 'complete', 
    label: 'Analysis Complete', 
    description: 'Your detailed performance report is ready',
    icon: CheckCircleOutline 
  }
];

/**
 * Step status types
 */
const STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  ERROR: 'error'
};

/**
 * An overlay component that shows the progress of the PageSpeed analysis
 */
const LoadingOverlay = ({ 
  progress = 0,
  message = '',
  subMessage = '',
  currentStep = 'initializing',
}) => {
  const theme = useTheme();
  const [visibleSteps, setVisibleSteps] = useState([]);
  const [displayedStep, setDisplayedStep] = useState(currentStep);
  
  // Transition to new step with a slight delay for animation
  useEffect(() => {
    if (currentStep !== displayedStep) {
      const timer = setTimeout(() => {
        setDisplayedStep(currentStep);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep, displayedStep]);
  
  // Determine the status of each step based on the current step
  useEffect(() => {
    // Normalize the input step (handle 'initialize' as 'initializing')
    const normalizedStep = displayedStep === 'initialize' ? 'initializing' : displayedStep;
    
    // Find the index of the current step
    const currentIndex = ANALYSIS_STEPS.findIndex(step => step.id === normalizedStep);
    
    if (currentIndex === -1) return;
    
    // Update the status of each step
    const updatedSteps = ANALYSIS_STEPS.map((step, index) => {
      let status = STATUS.PENDING;
      
      if (index < currentIndex) {
        status = STATUS.COMPLETED;
      } else if (index === currentIndex) {
        status = STATUS.ACTIVE;
      }
      
      return {
        ...step,
        status
      };
    });
    
    // Show only steps up to the current step plus one pending step
    const stepsToShow = updatedSteps.filter((step, index) => 
      index <= currentIndex + 1 || step.status === STATUS.COMPLETED
    );
    
    setVisibleSteps(stepsToShow);
  }, [displayedStep]);

  const getStepIcon = (status, StepIcon) => {
    switch (status) {
      case STATUS.COMPLETED:
        return <CheckCircleOutline fontSize="small" sx={{ color: theme.palette.success.main }} />;
      case STATUS.ACTIVE:
        return <HourglassTopTwoTone fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      case STATUS.ERROR:
        return <ErrorOutline fontSize="small" sx={{ color: theme.palette.error.main }} />;
      default:
        return <RadioButtonUnchecked fontSize="small" sx={{ color: theme.palette.text.disabled }} />;
    }
  };

  return (
    <Fade in={true} timeout={300}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          my: 4
        }}
      >
        <Card
          elevation={2}
          sx={{
            width: '100%',
            maxWidth: 700,
            mx: 'auto',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '8px',
              bgcolor: theme.palette.primary.light,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                bgcolor: theme.palette.primary.main,
                width: `${progress}%`,
                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </Box>
          
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CircularProgress 
                variant="determinate" 
                value={progress} 
                size={80} 
                thickness={4} 
                sx={{ 
                  mb: 2,
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)' 
                }}
              />
              <Typography 
                variant="h4" 
                component="h2"
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  transition: 'all 0.3s ease'
                }}
              >
                {message}
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  mt: 1,
                  transition: 'all 0.3s ease'
                }}
              >
                {subMessage}
              </Typography>
              
              <Chip 
                label={`${progress}% Complete`} 
                color="primary" 
                size="small"
                sx={{ 
                  mt: 2, 
                  transition: 'all 0.3s ease'
                }}
              />
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            <List sx={{ width: '100%' }}>
              {visibleSteps.map((step) => (
                <ListItem
                  key={step.id}
                  sx={{
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: step.status === STATUS.ACTIVE ? 
                      alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: step.status === STATUS.ACTIVE ? 'translateX(4px)' : 'translateX(0)',
                    opacity: step.status === STATUS.PENDING ? 0.7 : 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '42px' }}>
                    {getStepIcon(step.status, step.icon)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: step.status === STATUS.ACTIVE ? 600 : 400,
                          color: step.status === STATUS.ACTIVE ? 
                            theme.palette.text.primary : 
                            step.status === STATUS.COMPLETED ? 
                              theme.palette.text.secondary : 
                              theme.palette.text.disabled,
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {step.label}
                      </Typography>
                    }
                    secondary={
                      step.status !== STATUS.PENDING && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: step.status === STATUS.ACTIVE ? 
                              theme.palette.primary.main : 
                              theme.palette.text.secondary,
                            fontSize: '0.8rem',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {step.description}
                        </Typography>
                      )
                    }
                  />
                  <Box sx={{ ml: 1, minWidth: '60px', textAlign: 'right' }}>
                    {step.status === STATUS.ACTIVE && (
                      <CircularProgress size={20} thickness={5} />
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

// Helper function to create alpha channel colors
function alpha(color, opacity) {
  return color + Math.round(opacity * 255).toString(16).padStart(2, '0');
}

export default LoadingOverlay;